// Pre-run cost estimator for the new-run wizard. Counts real tokens (per the selected
// model's BPE encoding) from the actual stage-prompt bodies + the dataset transcripts,
// plus per-call chat + schema overhead, then prices it per model. A run executes BOTH
// the candidate and baseline prompt sets over every item, so the estimate counts both.
// See plan §6.
//
// Input tokens are exact once the tokenizer loads; OUTPUT tokens are still an estimate
// (unknowable pre-run) — the backend should return authoritative usage (plan §8).

import { MOCK_VERSIONS } from 'app/components/ops-prompts/mockData'
import type { DatasetItem } from 'app/components/ops-datasets/mockData'
import { MODEL_PRICING, costForUsage } from './pricing'
import { PIPELINE_STAGES, type Model, type PipelineStage, type PromptSet } from './types'

/** A token counter (model-aware, e.g. from useTokenCounter). */
export type CountTokens = (text: string | undefined, model: Model) => number

// Per-call request overhead beyond the visible text. Chat framing follows OpenAI's
// token-counting cookbook; the schema figure approximates the structured-output JSON
// schema/function definition sent on every call (the exact schema lives in the
// pipeline — a rough constant here, refined by real backend usage).
const TOKENS_PER_MESSAGE = 3
const REPLY_PRIMING = 3
const MESSAGES_PER_CALL = 2 // system prompt + user (transcript + prior-stage context)
const CHAT_OVERHEAD = TOKENS_PER_MESSAGE * MESSAGES_PER_CALL + REPLY_PRIMING
const SCHEMA_TOKENS = 300
const PER_CALL_FIXED = CHAT_OVERHEAD + SCHEMA_TOKENS

/** Fallback stage-prompt size (tokens) when a version body isn't in the mock store. */
const FALLBACK_PROMPT_TOKENS = 300

function promptTokens(key: PipelineStage | `single`, version: number, model: Model, count: CountTokens): number {
  const found = MOCK_VERSIONS.find((entry) => entry.promptKey === key && entry.version === version)
  return found ? count(found.body, model) : FALLBACK_PROMPT_TOKENS
}

/**
 * One side's per-item cost drivers: the prompt text it sends and how many model calls
 * it makes. The single prompt is one call; the stage pipeline makes one call per stage
 * that runs, each re-sending the transcript.
 */
function sideShape(
  set: PromptSet,
  stages: readonly PipelineStage[],
  model: Model,
  count: CountTokens,
): { prompt: number; calls: number } {
  if (set.single !== undefined) return { prompt: promptTokens(`single`, set.single, model, count), calls: 1 }
  return {
    prompt: stages.reduce((sum, stage) => sum + promptTokens(stage, set[stage], model, count), 0),
    calls: stages.length,
  }
}

export type ModelCostEstimate = {
  model: Model
  cost: number
  unpriced: boolean
  promptTokens: number
  completionTokens: number
}

export type CostEstimate = {
  items: number
  models: number
  /** Input tokens for the whole run (all items × both prompt sets × all models). */
  promptTokens: number
  completionTokens: number
  totalCost: number
  perModel: ModelCostEstimate[]
  /** True if any selected model has no entry in the pricing table. */
  hasUnpriced: boolean
}

/**
 * Estimate the cost of a run before starting it. Each stage that runs is one API call
 * that re-sends the transcript (plus prompt + overhead), and every item runs through
 * both the candidate and baseline prompt sets. Counting is per-model because encodings
 * differ across GPT families. `stages` defaults to the full pipeline.
 */
export function estimateRunCost(params: {
  items: DatasetItem[]
  models: Model[]
  candidate: PromptSet
  baseline: PromptSet
  countTokens: CountTokens
  stages?: readonly PipelineStage[]
}): CostEstimate {
  const { items, models, candidate, baseline, countTokens, stages = PIPELINE_STAGES } = params

  const perModel: ModelCostEstimate[] = models.map((model) => {
    const sides = [candidate, baseline].map((set) => sideShape(set, stages, model, countTokens))

    let promptTokens = 0
    let completionTokens = 0
    for (const item of items) {
      const perCallInput = countTokens(item.transcriptSnapshot, model) + PER_CALL_FIXED
      for (const side of sides) promptTokens += side.prompt + perCallInput * side.calls
      // Both pipelines produce the same fields, so the output estimate is the same.
      completionTokens += (item.golden.people.length * 45 + 140) * 2
    }

    return {
      model,
      cost: costForUsage(model, { promptTokens, completionTokens }),
      unpriced: !MODEL_PRICING[model],
      promptTokens,
      completionTokens,
    }
  })

  return {
    items: items.length,
    models: models.length,
    promptTokens: perModel.reduce((sum, entry) => sum + entry.promptTokens, 0),
    completionTokens: perModel.reduce((sum, entry) => sum + entry.completionTokens, 0),
    totalCost: perModel.reduce((sum, entry) => sum + entry.cost, 0),
    perModel,
    hasUnpriced: perModel.some((entry) => entry.unpriced),
  }
}
